/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-len */
import React, { Fragment } from "react";
import {
  Route,
  Redirect,
  useRouteMatch,
  useParams,
  useHistory,
  useLocation
} from "react-router-dom";
import shallow from "zustand/shallow";
import * as Sentry from "@sentry/react";

import {
  ENVIRONMENT_TYPES,
  PRODUCTS,
  WORK_PLATFORM_FEATURE_FLAGS,
  ANALYTICS_FEATURE_FLAGS
} from "base/constants";
import { useUserStore } from "base/stores";
import { TaskDelegationCheckWrapper } from "account/taskdelegationcheck";
import { LazyLoadComponent, DeferComponent } from "widgets/lazyloader";
import { LazyRemote } from "widgets/lazyremote";
import { ErrorBoundary } from "widgets/layouts";
import { hasFeatures } from "widgets/others";
import { singleSignOn } from "account/singlesignon";
import { isEmpty } from "base/utils";

import {
  BaseComponent,
  PublicRoute,
  PrivateRoute
} from "./components/base/index.jsx";
import { SupportChannel } from "./support_channel/index.jsx";

function AppWrapper() {
  return (
    <ErrorBoundary>
      <BaseComponent baseName="/view">
        <PublicRoute path="/public/form">
          <LazyRemote
            name="form"
            module="kfform/publicDataForm"
            componentName="PublicDataForm"
          />
        </PublicRoute>

        <PublicRoute path={["/public/process", "/public"]}>
          <LazyRemote
            name="process"
            module="kfprocess/processhome"
            componentName="ProcessHome"
          />
        </PublicRoute>

        <PrivateRoute path="/">
          <PrivateRoutes />
        </PrivateRoute>
      </BaseComponent>
    </ErrorBoundary>
  );
}
export const App = Sentry.withProfiler(AppWrapper);

function PrivateRoutes() {
  let { flowControl, analyticsPreference, currentEnv } = useUserStore(
    (state) => ({
      flowControl: state.currentUser.AccountDetails.FlowControl,
      analyticsPreference: state.currentUser.AccountDetails.AnalyticsPreference,
      currentEnv: state.currentEnvironment
    }),
    shallow
  );

  const isFlowEnabled = (flowType) => {
    // Note: Flows, except for Application, Portal and Dataset, are not enabled in the Sandbox account.
    const isFlowEnabledInEnvironment =
      currentEnv.Type === ENVIRONMENT_TYPES.PRODUCTION;

    if (!isFlowEnabledInEnvironment) return false;

    const isAnalyticsPreferenceEnabled =
      analyticsPreference && analyticsPreference.Status === "Active";
    const isSuperAdmin = useUserStore.getState().isSuperAdmin();

    if (flowType === PRODUCTS.ANALYTICS) {
      return (
        (hasFeatures([ANALYTICS_FEATURE_FLAGS.REPORT_CREATE_TABLE]) &&
          isAnalyticsPreferenceEnabled) ||
        (hasFeatures([ANALYTICS_FEATURE_FLAGS.REPORT_CREATE_TABLE]) &&
          !isAnalyticsPreferenceEnabled &&
          isSuperAdmin)
      );
    }

    return !(
      flowControl &&
      flowControl[flowType] &&
      flowControl[flowType].IsDisabled
    );
  };

  return (
    <React.Fragment>
      <LoadTopNav />
      <LoadLeftNav />
      {hasFeatures([WORK_PLATFORM_FEATURE_FLAGS.ADMIN_ALLOW_AI_ASSIST]) && (
        <LazyRemote
          name="assist"
          module="kfassist/floatingWidget"
          componentName="AssistComponent"
        />
      )}
      <DeferComponent>
        <TaskDelegationCheckWrapper />
      </DeferComponent>

      <Route path="/home" exact={true}>
        <LazyLoadComponent
          component={() => import("./home_page/index.jsx")}
          componentName="HomePage"
        />
      </Route>
      <Route path="/home/explorer">
        <LazyRemote
          name={"platform"}
          module="kfplatform/explorer"
          componentName="ExplorerHome"
        />
      </Route>
      <Route path={"/myitems"}>
        <LazyLoadComponent
          component={() => import("kfplatform/globaltask")}
          componentName="GlobalTasks"
        />
      </Route>
      <Route path={"/bot/consent"}>
        <LazyLoadComponent
          component={() => import("account/botconsent")}
          componentName="BotConsent"
        />
      </Route>
      <Route
        path={[
          "/filepreview/:flowtype/:flowid/:reportid/:instanceid/:activityinstanceid/:fieldid/:tableid/:rowid",
          "/filepreview/:flowtype/:flowid/:instanceid/:stepid/:fieldid/:tableid/:rowid",
          "/filepreview/:flowtype/:flowid/:reportid/:instanceid/:activityinstanceid/:fieldid",
          "/filepreview/:flowtype/:flowid/:instanceid/:stepid/:fieldid",
          "/filepreview/:flowtype/:flowid/:instanceid/:fieldid",
          "/filepreview/:flowtype/:flowid/:instanceid"
        ]}
      >
        <LazyLoadComponent
          component={() => import("kfplatform/filePreview")}
          componentName="FilePreviewInNewTab"
        />
      </Route>
      {hasFeatures([WORK_PLATFORM_FEATURE_FLAGS.APP_ALLOW_APP_MODULE]) && (
        <Route path="/application">
          <LazyRemote
            name="application"
            module="kfapplication/applicationHome"
            componentName="ApplicationHome"
          />
        </Route>
      )}
      <Route path="/developerplatform/appstore">
        <LazyRemote
          name="appstore"
          module="kfappstore/appstorePlatform"
          componentName="AppstorePlatform"
        />
      </Route>
      {hasFeatures([
        WORK_PLATFORM_FEATURE_FLAGS.EXTERNALDATA_ALLOW_EXTERNALDATA_MODULE
      ]) && (
        <Route path="/externaldata/connection">
          <LazyRemote
            name="externaldata"
            module="kfexternaldata/connection"
            componentName="Connections"
          />
        </Route>
      )}
      {currentEnv.Type === ENVIRONMENT_TYPES.PRODUCTION && (
        <Route path="/appstore">
          <LazyRemote
            name="appstore"
            module="kfappstore/appstoreHome"
            componentName="AppstoreHome"
          />
        </Route>
      )}
      {isFlowEnabled(PRODUCTS.ANALYTICS) && (
        <Route path="/analytics">
          <LazyRemote
            name="analytics"
            module="kfanalytics/analyticshome"
            componentName="AnalyticsHome"
          />
        </Route>
      )}
      {isFlowEnabled(PRODUCTS.PROCESS) && (
        <Route path="/process">
          <LazyRemote
            name="process"
            module="kfprocess/processhome"
            componentName="ProcessHome"
          />
        </Route>
      )}
      {isFlowEnabled(PRODUCTS.CASE) && (
        <Route path="/case">
          <LazyRemote
            name="case"
            module="kfcase/casehome"
            componentName="CaseHome"
          />
        </Route>
      )}
      <Route path="/dataset">
        <LazyRemote
          name="dataset"
          module="dataset/datasetHome"
          componentName="DatasetHome"
        />
      </Route>
      {isFlowEnabled(PRODUCTS.INTEGRATION) && (
        <Route path="/integration">
          <LazyRemote
            name="integration"
            module="integration/integrationHome"
            componentName="IntegrationHome"
          />
        </Route>
      )}
      <Route path={["/account", "/mysettings"]}>
        {/* replace the test loader with respective module home component */}
        <LazyRemote
          name="account"
          module="account/accounthome"
          componentName="AccountHome"
        />
      </Route>
      <Route path="/backup/:backupid/file/:fileid/download">
        <LazyLoadComponent
          component={() => import("account/downloadbackupfile")}
          componentName="DownloadBackupFile"
        />
      </Route>
      <Route path={["/portal"]}>
        <LazyRemote
          name="account"
          module="account/accounthome"
          componentName="PortalAdminHome"
        />
      </Route>
      <Route
        path={[
          "/auditlog/download/:token/:fileName",
          "/auditlog/download/:token"
        ]}
      >
        <LazyLoadComponent
          component={() => import("kfplatform/exportFile")}
          componentName="AuditExportBanner"
        />
      </Route>
      <Route path="/notification">
        <LazyLoadComponent
          component={() =>
            import(/* webpackPreload: true */ "kfplatform/notification")
          }
          componentName="NotificationCenter"
        />
      </Route>
      <Route path="/list">
        <LazyRemote
          name="list"
          module="list/listHome"
          componentName="ListHome"
        />
      </Route>
      <Route path="/search">
        <LazyLoadComponent
          component={() => import("kfplatform/search/searchComponent")}
          componentName="SearchComponent"
        />
      </Route>

      <Route
        path={["/project/:projectId/form/:itemId", "/project/:projectId"]}
        render={({ match }) => {
          let projectId = match.params.projectId;
          let itemId = match.params.itemId;

          if (itemId) {
            return <Redirect to={`/case/${projectId}/form/${itemId}`} />;
          }

          return <Redirect to={`/case/${projectId}/home`} />;
        }}
      />

      {/* Note : This route is to automaticaly authenticate the sandbox account */}
      {currentEnv.Type === ENVIRONMENT_TYPES.PRODUCTION && (
        <Route exact path="/sandboxAuth/:sandboxId">
          <SandboxAuth />
        </Route>
      )}
      <DeferComponent>
        <LazyLoadComponent
          component={() =>
            import(/* webpackPreload: true */ "kfplatform/notification")
          }
          componentName="NotificationHome"
        />
      </DeferComponent>
      <DeferComponent>
        <LazyLoadComponent
          component={() => import("kfplatform/search/searchHome")}
          componentName="SearchHome"
        />
      </DeferComponent>
      <HomePageNavigator />
      <SupportChannel />
    </React.Fragment>
  );
}

function HomePageNavigator() {
  let match = useRouteMatch("/", {
    exact: true
  });
  //to solve localDev reload issue
  let env = process.env.REACT_APP_ENV;
  let pathName = "";
  let localMatch = window.location.pathname.includes("client");
  if (localMatch && window.location.pathname !== "/client") {
    pathName = window.location.pathname.replace("/client", "");
  } else if (window.location.pathname === "/client") {
    pathName = "/home";
  }

  return (
    <React.Fragment>
      {match.isExact && <Redirect to="/home" />}
      {env === "Development" && pathName.length > 0 && (
        <Redirect to={pathName} />
      )}
    </React.Fragment>
  );
}

function LoadLeftNav() {
  if (hasFeatures([WORK_PLATFORM_FEATURE_FLAGS.KISSFLOW_ALLOW_LEFT_NAV])) {
    window.isLeftNavEnabled = true;

    return (
      <LazyLoadComponent
        component={() => import("./components/leftnav/index.jsx")}
        componentName="LeftNav"
      />
    );
  }
  return "";
}

function LoadTopNav() {
  const showOldTopNav = !hasFeatures([
    WORK_PLATFORM_FEATURE_FLAGS.KISSFLOW_ALLOW_LEFT_NAV
  ]);

  return showOldTopNav ? (
    <Fragment>
      <LazyLoadComponent
        component={() => import("widgets/topnav")}
        componentName="CommonTopnav"
      />
      <LazyRemote
        name="process"
        module="kfprocess/processtopnav"
        componentName="ProcessTopNav"
      />
    </Fragment>
  ) : (
    <LazyLoadComponent
      component={() => import("./work_platform/top.nav.jsx")}
      componentName="WorkPlatformTopNav"
    />
  );
}

/**
 * Note : This logic should move to server in future.
 */
function SandboxAuth() {
  const { sandboxId } = useParams();
  const history = useHistory();
  const location = useLocation();

  const sandboxes = useUserStore((state) => state.sandbox);

  const sandboxEnv = sandboxes.find((env) => env._id === sandboxId);

  React.useEffect(
    function onLoad() {
      if (!isEmpty(sandboxEnv)) {
        const { search = "" } = location;
        let redirectPath = search.split("redirect_path=")[1] || "";
        let sandboxEndpoint = sandboxEnv.CustomDomain || sandboxEnv.Endpoint;

        singleSignOn(`${sandboxEndpoint}${redirectPath}`)
          .then((signinUrl) => {
            window.location.replace(signinUrl);
          })
          .catch((e) => {
            history.push("/view/home");
          });
      }
    },
    [sandboxEnv]
  );

  return null;
}
